import * as React from 'react';
import { useAuth } from '../contexts/AuthProvider';
import { useNavigation } from '../contexts/NavigationProvider';
import { useLanguage } from '../contexts/LanguageProvider';
import type { User } from '../types';
import { AroundMeSection } from './AroundMeSection';

const { useMemo } = React;

const RightSidebar: React.FC = () => {
  const { currentUser, users } = useAuth();
  const { handleNavigation, handleViewProfile } = useNavigation();
  const { t } = useLanguage();

  const userMap = useMemo(() => new Map(users.map(u => [u.handle, u])), [users]);

  const myEndorsements = useMemo(() => {
    return (currentUser?.endorsements || []).map((handle: string) => userMap.get(handle)).filter(Boolean) as User[];
  }, [currentUser, userMap]);

  const whoEndorsedMe = useMemo(() => {
    return (currentUser?.endorsedBy || []).map((handle: string) => userMap.get(handle)).filter(Boolean) as User[];
  }, [currentUser, userMap]);

  if (!currentUser) {
    return null; 
  }

  return (
    <aside className="space-y-6 font-urbanist">
      <AroundMeSection />

      <div className="bg-brand-surface rounded-lg p-4 border border-gray-800 shadow-sm">
        <button 
          onClick={() => handleNavigation('network', { tab: 'endorsing' })}
          className="w-full text-left text-sm font-black text-white mb-4 hover:text-brand-orange transition-colors uppercase tracking-tighter"
        >
          {t('MY_ENDORSEMENTS')} ({myEndorsements.length})
        </button>
        {myEndorsements.length > 0 ? (
            <ul className="space-y-4">
              {myEndorsements.slice(0, 2).map((user) => (
                <li key={user.handle} className="flex items-center space-x-3 cursor-pointer group" onClick={() => handleViewProfile(user)}>
                  <img src={user.avatarUrl} alt={user.name} className="h-10 w-10 rounded-full border border-gray-700 group-hover:border-brand-orange transition-colors" />
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-white group-hover:underline truncate text-sm tracking-tight">{user.name}</p>
                    <p className="text-xs text-brand-muted truncate font-mono">@{user.handle}</p>
                  </div>
                </li>
              ))}
            </ul>
        ) : (
            <p className="text-sm text-brand-muted italic">You haven't endorsed anyone yet.</p>
        )}
      </div>
      
      <div className="bg-brand-surface rounded-lg p-4 border border-gray-800 shadow-sm">
        <button 
          onClick={() => handleNavigation('network', { tab: 'followers' })}
          className="w-full text-left text-sm font-black text-white mb-4 hover:text-brand-orange transition-colors uppercase tracking-tighter"
        >
          {t('WHO_ENDORSED_ME')} ({whoEndorsedMe.length})
        </button>
        {whoEndorsedMe.length > 0 ? (
            <ul className="space-y-4">
              {whoEndorsedMe.slice(0, 2).map((user) => (
                <li key={user.handle} className="flex items-center space-x-3 cursor-pointer group" onClick={() => handleViewProfile(user)}>
                  <img src={user.avatarUrl} alt={user.name} className="h-10 w-10 rounded-full border border-gray-700 group-hover:border-brand-orange transition-colors" />
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-white group-hover:underline truncate text-sm tracking-tight">{user.name}</p>
                    <p className="text-xs text-brand-muted truncate font-mono">@{user.handle}</p>
                  </div>
                </li>
              ))}
            </ul>
        ) : (
            <p className="text-sm text-brand-muted italic">No one has endorsed you yet.</p>
        )}
      </div>
    </aside>
  );
};

export default RightSidebar;